// First-party telemetry only. No third-party analytics, form contents or query strings.
import { useEffect, useRef } from 'react';
import api from '@/lib/api';

let currentSession = null;
const uid = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}.${Math.random().toString(36).slice(2)}`;

export function moduleForPath(path, modules = {}) {
  if (path.startsWith('/portal/poster')) return 'poster';
  if (path.startsWith('/portal/invoices')) return 'invoicing';
  if (path.startsWith('/portal/marketing')) return 'marketing';
  if (path.startsWith('/portal/bookings')) return null; // explicitly excluded: undeveloped
  if (path.startsWith('/portal/territory/basic')) return 'territory';
  if (path.startsWith('/portal/territory')) return modules.territory_plus ? 'territory_plus' : 'territory';
  if (path.startsWith('/portal/files')) return 'files';
  if (path.startsWith('/portal/training')) return 'video';
  if (path.startsWith('/portal/updates')) return 'updates';
  if (path.startsWith('/portal/events') || path.startsWith('/portal/calendar')) return 'calendar';
  if (path.startsWith('/portal/shape-orders')) return 'store';
  if (path.startsWith('/portal/contracts')) return 'contracts';
  if (path.startsWith('/portal/account')) return 'account';
  if (path === '/portal' || path === '/portal/' || path.startsWith('/portal/details')) return 'franchise';
  return null;
}

// Optional nested tools can override the shell module, e.g. Poster Studio modal.
// Always release the override when that tool unmounts.
let overrideModule = null;
export function setHubUsageModule(module) {
  overrideModule = module;
  window.dispatchEvent(new Event('hub-usage-module'));
  return () => {
    overrideModule = null;
    window.dispatchEvent(new Event('hub-usage-module'));
  };
}

export function useHubUsage({ user, ready, path, modules }) {
  const current = useRef({ path, modules });
  current.current = { path, modules };
  const visitRef = useRef(null);

  useEffect(() => {
    if (!ready || user?.role !== 'franchisee') return undefined;
    let alive = true;
    let lastActivity = Date.now();
    let lastTick = Date.now();
    let lastView = '';
    let lastPersist = 0;
    let session = { id: uid(), last: Date.now() };
    // Keep one tab session across reloads; rotate after 30m of inactivity or
    // a different account signs in on the same browser.
    try {
      const old = JSON.parse(sessionStorage.getItem('cm.usage.session') || 'null');
      if (old?.actor === user.id && Date.now() - old.last < 1800000) session = old;
    } catch { /* storage is optional */ }
    const persist = () => {
      session.actor = user.id;
      currentSession = session.id;
      try { sessionStorage.setItem('cm.usage.session', JSON.stringify(session)); } catch { /* optional */ }
    };
    persist();
    const send = (kind, seconds = 0) => {
      const module = overrideModule || moduleForPath(current.current.path, current.current.modules);
      if (!alive || !module) return;
      const event = { id: uid(), module, kind, session_id: session.id, seconds };
      // Best effort. Do not queue/replay old heartbeats as new activity.
      api.post('/portal/usage/events', { events: [event] }, { _hubTelemetry: true }).catch(() => {});
    };
    const visit = () => {
      const key = `${overrideModule || ''}:${current.current.path}:${Boolean(current.current.modules?.territory_plus)}`;
      if (key === lastView || document.visibilityState !== 'visible') return;
      lastView = key;
      send('page_view');
    };
    visitRef.current = visit;
    const activity = () => {
      const now = Date.now();
      if (now - session.last > 1800000) {
        session = { id: uid(), last: now };
        lastView = '';
        visit();
      }
      lastActivity = now;
      session.last = now;
      if (now - lastPersist > 5000) { persist(); lastPersist = now; }
    };
    const focus = () => {
      lastTick = Date.now();
      if (document.visibilityState === 'visible' && document.hasFocus()) {
        activity();
        visit();
      }
    };
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = (now - lastTick) / 1000;
      lastTick = now;
      // No time for hidden tabs, unfocused windows, 2-minute idle periods,
      // sleep/wake gaps or suspended JS timers.
      if (document.visibilityState === 'visible' && document.hasFocus() && now - lastActivity < 120000 && elapsed >= 20 && elapsed <= 35) {
        send('heartbeat', Math.min(30, Math.round(elapsed)));
        session.last = now;
        persist();
      }
    }, 30000);
    const interceptor = api.interceptors.request.use(config => {
      if (!config._hubTelemetry && currentSession) {
        config.headers = config.headers || {};
        config.headers['X-Hub-Session'] = currentSession;
      }
      return config;
    });
    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(name => window.addEventListener(name, activity, { passive: true }));
    window.addEventListener('focus', focus);
    window.addEventListener('hub-usage-module', visit);
    document.addEventListener('visibilitychange', focus);
    visit();
    return () => {
      alive = false;
      clearInterval(interval);
      api.interceptors.request.eject(interceptor);
      events.forEach(name => window.removeEventListener(name, activity));
      window.removeEventListener('focus', focus);
      window.removeEventListener('hub-usage-module', visit);
      document.removeEventListener('visibilitychange', focus);
      visitRef.current = null;
      currentSession = null;
      overrideModule = null;
    };
  }, [user?.id, user?.role, ready]);

  useEffect(() => { visitRef.current?.(); }, [path, modules?.territory_plus]);
}
